(function () {
  'use strict';

  var variantCompareAtCache = {};
  var initialized = false;
  var observerAttached = false;
  var isApplyingUpdates = false;
  var observerDebounce = null;
  var RETRY_DELAYS_MS = [0, 300, 700, 1500, 2500];

  var SELECTORS = {
    v2: {
      cartItems: '.upcart-public-cart-items',
      cartSummary: '.upcart-public-cart-summary',
      cartFooter: '.upcart-public-cart-footer, .upcart-checkout-button-container, [class*="upcart-public"][class*="footer"]',
      footerDiscount: '.upcart-public-cart-summary__total-discount',
      subtotal: '.upcart-public-cart-summary__subtotal, .upcart-public-footer__subtotal, .upcart-subtotal-value, .upcart-subtotal-amount',
      checkoutButton: '.upcart-checkout-button, .upcart-public-checkout-button',
      tile: '.upcart-public-component-product-tile',
      compareAt: '.upcart-public-component-product-tile__price-compare-at',
      price: '.upcart-public-component-product-tile__price',
      savings: '.upcart-public-component-product-tile__savings',
      pricing: '.upcart-public-component-product-tile__product-pricing'
    },
    v1: {
      cartItems: '.upcart-products-section',
      cartSummary: '.upcart-savings-row',
      cartFooter: '.upcart-checkout-button-container, .upcart-footer',
      footerDiscount: '.upcart-savings-amount',
      subtotal: '.upcart-subtotal-amount, .upcart-cart-subtotal-value, .cart-subtotal-value',
      checkoutButton: '.upcart-checkout-button',
      tile: '.upcart-product-item',
      compareAt: '.upcart-item-compare-price',
      price: '.upcart-item-price',
      savings: '.upcart-product-savings',
      pricing: '.upcart-item-prices-row'
    }
  };

  function getRoot() {
    return window.upcartDocumentOrShadowRoot || document;
  }

  function formatMoney(cents) {
    var amount = Math.round(Number(cents));

    if (!Number.isFinite(amount) || amount <= 0) {
      return null;
    }

    if (typeof window.upcartFormatMoney === 'function') {
      return window.upcartFormatMoney({ cents: amount });
    }

    if (window.theme && window.theme.Currency && typeof window.theme.Currency.formatMoney === 'function') {
      return window.theme.Currency.formatMoney(amount, window.theme.settings.moneyFormat);
    }

    return '$' + (amount / 100).toFixed(2);
  }

  function getVariantId(item) {
    return item.variant_id || item.variantId || item.id;
  }

  function getProductHandle(item) {
    if (item.handle) {
      return item.handle;
    }

    var url = item.url || '';
    var match = String(url).match(/\/products\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  function getUnitSalePrice(item) {
    return Number(item.price) || 0;
  }

  function getPropertyCompareAt(item) {
    var props = item.properties || {};
    var raw = props._unit_compare_at_price;

    if (raw == null) {
      raw = props['_unit_compare_at_price'];
    }

    if (raw == null || raw === '') {
      return null;
    }

    var parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  function getListLinePrice(item, compareAtUnit) {
    var quantity = Number(item.quantity) || 0;
    var unitSalePrice = getUnitSalePrice(item);
    var saleSubtotal = unitSalePrice * quantity;
    var listLine = Number(item.original_line_price) || saleSubtotal;

    if (compareAtUnit > unitSalePrice) {
      var compareAtLine = compareAtUnit * quantity;
      if (compareAtLine > listLine) {
        listLine = compareAtLine;
      }
    }

    // Trust Shopify's cart for the final price: final_line_price already
    // reflects the automatic bundle discount at the current quantity (full
    // price below the pack threshold, discounted at/above it). Recomputing the
    // percent client-side wrongly kept the discount when qty dropped to 1.
    var finalLine = Number(item.final_line_price) || saleSubtotal;

    return { listLine: listLine, finalLine: finalLine };
  }

  function fetchCompareAtFromProduct(item) {
    var variantId = getVariantId(item);

    if (!variantId) {
      return Promise.resolve(0);
    }

    if (variantCompareAtCache[variantId] !== undefined) {
      return Promise.resolve(variantCompareAtCache[variantId]);
    }

    var handle = getProductHandle(item);

    function storeCompareAt(value) {
      variantCompareAtCache[variantId] = value;
      return value;
    }

    if (handle) {
      return fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
        .then(function (res) {
          return res.ok ? res.json() : null;
        })
        .then(function (product) {
          if (!product || !product.variants) {
            return storeCompareAt(0);
          }

          var variant = product.variants.find(function (v) {
            return String(v.id) === String(variantId);
          });
          var compareAt = variant && Number(variant.compare_at_price) > 0
            ? Number(variant.compare_at_price)
            : 0;

          return storeCompareAt(compareAt);
        })
        .catch(function () {
          return storeCompareAt(0);
        });
    }

    return fetch('/variants/' + variantId + '.js', { credentials: 'same-origin' })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (variant) {
        var compareAt = variant && Number(variant.compare_at_price) > 0
          ? Number(variant.compare_at_price)
          : 0;
        return storeCompareAt(compareAt);
      })
      .catch(function () {
        return storeCompareAt(0);
      });
  }

  function resolveCompareAtUnitPrice(item) {
    var fromProperty = getPropertyCompareAt(item);

    if (fromProperty != null) {
      return Promise.resolve(fromProperty);
    }

    return fetchCompareAtFromProduct(item);
  }

  function detectVersion(root) {
    if (root.querySelector(SELECTORS.v2.tile)) {
      return SELECTORS.v2;
    }
    if (root.querySelector(SELECTORS.v1.tile)) {
      return SELECTORS.v1;
    }
    return null;
  }

  function findTileForItem(root, item, selectors, index, usedTiles) {
    var variantId = String(getVariantId(item) || '');
    var handle = getProductHandle(item);
    var tiles = root.querySelectorAll(selectors.tile);
    usedTiles = usedTiles || [];

    for (var t = 0; t < tiles.length; t++) {
      var tile = tiles[t];

      // Skip tiles already claimed by an earlier line item so two lines sharing
      // a variant (differing only by properties) don't collide on one tile.
      if (usedTiles.indexOf(tile) !== -1) {
        continue;
      }

      var links = tile.querySelectorAll('a[href]');

      for (var l = 0; l < links.length; l++) {
        var href = links[l].getAttribute('href') || '';

        if (variantId && href.indexOf('variant=' + variantId) !== -1) {
          usedTiles.push(tile);
          return tile;
        }

        if (handle && href.indexOf('/products/' + handle) !== -1 && tiles.length === 1) {
          usedTiles.push(tile);
          return tile;
        }
      }
    }

    // Positional fallback: prefer the tile at this cart index if still
    // unclaimed, otherwise the first unclaimed tile.
    var fallback = tiles[index];
    if (!fallback || usedTiles.indexOf(fallback) !== -1) {
      fallback = null;
      for (var f = 0; f < tiles.length; f++) {
        if (usedTiles.indexOf(tiles[f]) === -1) {
          fallback = tiles[f];
          break;
        }
      }
    }

    if (fallback) {
      usedTiles.push(fallback);
    }

    return fallback || null;
  }

  function showElement(el) {
    if (!el) return;
    el.hidden = false;
    el.style.removeProperty('display');
    el.style.removeProperty('visibility');
  }

  function hideElement(el) {
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
    el.style.display = 'none';
  }

  function ensureCompareAtElement(tile, selectors, listFormatted) {
    var compareEl = tile.querySelector(selectors.compareAt);

    if (compareEl) {
      return compareEl;
    }

    var pricingEl = tile.querySelector(selectors.pricing);
    var priceEl = tile.querySelector(selectors.price);

    if (!pricingEl || !priceEl) {
      return null;
    }

    compareEl = document.createElement('span');
    compareEl.className = selectors.compareAt.replace(/^\./, '');
    compareEl.textContent = listFormatted;
    pricingEl.insertBefore(compareEl, priceEl);
    return compareEl;
  }

  function ensureSavingsElement(tile, selectors, savingsText) {
    var savingsEl = tile.querySelector(selectors.savings);

    if (savingsEl) {
      return savingsEl;
    }

    var pricingEl = tile.querySelector(selectors.pricing);

    if (!pricingEl) {
      return null;
    }

    savingsEl = document.createElement('div');
    savingsEl.className = selectors.savings.replace(/^\./, '');
    savingsEl.textContent = savingsText;
    pricingEl.appendChild(savingsEl);
    return savingsEl;
  }

  function formatDiscountAmount(cents) {
    var formatted = formatMoney(cents);

    if (!formatted) {
      return null;
    }

    return '- ' + formatted;
  }

  function queryFirst(root, selectorList) {
    if (!root || !selectorList) {
      return null;
    }

    var selectors = selectorList.split(',');

    for (var i = 0; i < selectors.length; i++) {
      var el = root.querySelector(selectors[i].trim());

      if (el) {
        return el;
      }
    }

    return null;
  }

  function isSubtotalLabel(text) {
    return /^subtotal:?\s*$/i.test(String(text || '').trim());
  }

  function looksLikePrice(text) {
    return /\$\s*[\d,]+(?:\.\d{2})?/.test(String(text || '').trim());
  }

  function findPriceInContainer(container) {
    if (!container) {
      return null;
    }

    var nodes = container.querySelectorAll('span, div, p, strong, b');

    for (var i = nodes.length - 1; i >= 0; i--) {
      var node = nodes[i];
      var text = (node.textContent || '').trim();

      if (looksLikePrice(text) && !/^subtotal$/i.test(text) && !/^discounts?$/i.test(text)) {
        return node;
      }
    }

    return null;
  }

  function findSubtotalElement(root, selectors) {
    var subtotalEl = queryFirst(root, selectors.subtotal);

    if (subtotalEl) {
      return subtotalEl;
    }

    var labels = root.querySelectorAll('span, div, p, strong, b, label');

    for (var i = 0; i < labels.length; i++) {
      var label = labels[i];

      if (!isSubtotalLabel(label.textContent)) {
        continue;
      }

      var row = label.closest('div, section, li, tr, footer');

      if (!row) {
        continue;
      }

      var priceEl = findPriceInContainer(row);

      if (priceEl) {
        return priceEl;
      }
    }

    return null;
  }

  function findCheckoutButton(root, selectors) {
    var checkoutEl = queryFirst(root, selectors.checkoutButton);

    if (checkoutEl && /checkout/i.test(checkoutEl.textContent || '')) {
      return checkoutEl;
    }

    var buttons = root.querySelectorAll('button, [role="button"], a');

    for (var i = 0; i < buttons.length; i++) {
      var button = buttons[i];
      var text = (button.textContent || '').trim();

      if (/checkout/i.test(text) && looksLikePrice(text)) {
        return button;
      }
    }

    return checkoutEl;
  }

  function updateCheckoutButtonPrice(button, formattedTotal) {
    if (!button || !formattedTotal) {
      return false;
    }

    var text = (button.textContent || '').trim();

    if (/•/.test(text)) {
      button.textContent = text.replace(/•\s*.+$/, '• ' + formattedTotal);
      return true;
    }

    if (looksLikePrice(text)) {
      button.textContent = text.replace(/\$\s*[\d,]+(?:\.\d{2})?/, formattedTotal);
      return true;
    }

    return false;
  }

  function updateFooterDiscount(totalSavings, root, selectors) {
    if (!root || !selectors) {
      return false;
    }

    var discountEl = root.querySelector(selectors.footerDiscount);

    // Savings gone: clear a discount amount we wrote on an earlier run so it
    // doesn't linger on the footer once the bundle discount no longer applies.
    if (totalSavings <= 0) {
      if (discountEl && discountEl.getAttribute('data-ausvitality-pricing-applied') === 'true') {
        discountEl.textContent = '';
        discountEl.removeAttribute('data-ausvitality-pricing-applied');
      }
      return false;
    }

    if (!discountEl) {
      return false;
    }

    var formatted = formatDiscountAmount(totalSavings);

    if (!formatted) {
      return false;
    }

    discountEl.textContent = formatted;
    discountEl.setAttribute('data-ausvitality-pricing-applied', 'true');
    return true;
  }

  function updateFooterSubtotal(totalFinal, root, selectors) {
    if (!root || !selectors || totalFinal <= 0) {
      return false;
    }

    var formatted = formatMoney(totalFinal);

    if (!formatted) {
      return false;
    }

    var subtotalEl = findSubtotalElement(root, selectors);
    var checkoutEl = findCheckoutButton(root, selectors);
    var updated = false;

    if (subtotalEl) {
      subtotalEl.textContent = formatted;
      subtotalEl.setAttribute('data-ausvitality-pricing-applied', 'true');
      updated = true;
    }

    if (checkoutEl) {
      if (updateCheckoutButtonPrice(checkoutEl, formatted)) {
        checkoutEl.setAttribute('data-ausvitality-pricing-applied', 'true');
        updated = true;
      }
    }

    return updated;
  }

  function updateTile(tile, listLine, finalLine, selectors) {
    if (!tile || !Number.isFinite(listLine) || !Number.isFinite(finalLine)) {
      return false;
    }

    // No discount to show: clear any compare-at / savings we added on an earlier
    // run so a prior discounted state doesn't linger after qty drops below the
    // bundle threshold.
    if (listLine <= finalLine) {
      hideElement(tile.querySelector(selectors.compareAt));
      hideElement(tile.querySelector(selectors.savings));
      tile.removeAttribute('data-ausvitality-pricing-applied');
      return false;
    }

    var listFormatted = formatMoney(listLine);
    var finalFormatted = formatMoney(finalLine);
    var savingsFormatted = formatMoney(listLine - finalLine);

    if (!listFormatted || !finalFormatted || !savingsFormatted) {
      return false;
    }

    var savingsText = '(Save ' + savingsFormatted + ')';
    var compareEl = ensureCompareAtElement(tile, selectors, listFormatted);
    var priceEl = tile.querySelector(selectors.price);
    var savingsEl = ensureSavingsElement(tile, selectors, savingsText);

    if (compareEl) {
      compareEl.textContent = listFormatted;
      showElement(compareEl);
    }

    if (priceEl) {
      priceEl.textContent = finalFormatted;
    }

    if (savingsEl) {
      savingsEl.textContent = savingsText;
      showElement(savingsEl);
    }

    tile.setAttribute('data-ausvitality-pricing-applied', 'true');
    return true;
  }

  function getShopifyCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) {
        throw new Error('cart.js request failed');
      }
      return res.json();
    });
  }

  function fixLineItemPricing() {
    return getShopifyCart()
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) {
          return;
        }

        var root = getRoot();
        var selectors = detectVersion(root);

        if (!selectors) {
          return;
        }

        isApplyingUpdates = true;

        var usedTiles = [];
        var itemPromises = cart.items.map(function (item, index) {
          // Claim the tile synchronously in cart order (before the async
          // compare-at lookup) so same-variant lines map to distinct tiles.
          var tile = findTileForItem(root, item, selectors, index, usedTiles);
          return resolveCompareAtUnitPrice(item).then(function (compareAtUnit) {
            var prices = getListLinePrice(item, compareAtUnit);

            updateTile(tile, prices.listLine, prices.finalLine, selectors);
            return prices;
          });
        });

        return Promise.all(itemPromises)
          .then(function (allPrices) {
            var totalSavings = 0;
            var totalFinal = 0;

            allPrices.forEach(function (prices) {
              totalFinal += prices.finalLine;

              if (prices.listLine > prices.finalLine) {
                totalSavings += prices.listLine - prices.finalLine;
              }
            });

            updateFooterDiscount(totalSavings, root, selectors);
            updateFooterSubtotal(totalFinal, root, selectors);
          })
          .finally(function () {
            setTimeout(function () {
              isApplyingUpdates = false;
            }, 100);
          });
      })
      .catch(function () {
        isApplyingUpdates = false;
      });
  }

  function attachObserver() {
    if (observerAttached || typeof MutationObserver === 'undefined') {
      return;
    }

    var root = getRoot();
    var selectors = detectVersion(root);

    if (!selectors) {
      return;
    }

    var observeTargets = [
      root.querySelector(selectors.cartItems),
      root.querySelector(selectors.cartSummary),
      queryFirst(root, selectors.cartFooter)
    ].filter(Boolean);

    if (!observeTargets.length) {
      return;
    }

    var observer = new MutationObserver(function () {
      if (isApplyingUpdates) {
        return;
      }

      if (observerDebounce) {
        clearTimeout(observerDebounce);
      }

      observerDebounce = setTimeout(function () {
        fixLineItemPricing();
      }, 400);
    });

    observeTargets.forEach(function (target) {
      observer.observe(target, { childList: true, subtree: true, characterData: true });
    });

    observerAttached = true;
  }

  function scheduleUpdate() {
    RETRY_DELAYS_MS.forEach(function (delay) {
      setTimeout(function () {
        fixLineItemPricing().then(function () {
          attachObserver();
        });
      }, delay);
    });
  }

  function subscribeToUpcart() {
    if (initialized) {
      return true;
    }

    if (typeof window.upcartSubscribeCartLoaded !== 'function') {
      return false;
    }

    window.upcartSubscribeCartLoaded(function () {
      scheduleUpdate();
    });

    if (typeof window.upcartSubscribeCartUpdated === 'function') {
      window.upcartSubscribeCartUpdated(function () {
        scheduleUpdate();
      });
    }

    if (typeof window.upcartSubscribeCartOpened === 'function') {
      window.upcartSubscribeCartOpened(function () {
        scheduleUpdate();
      });
    }

    if (typeof window.upcartSubscribeAddedToCart === 'function') {
      window.upcartSubscribeAddedToCart(function () {
        scheduleUpdate();
      });
    }

    initialized = true;
    return true;
  }

  var attempts = 0;
  var pollInterval = setInterval(function () {
    if (subscribeToUpcart() || ++attempts >= 60) {
      clearInterval(pollInterval);
    }
  }, 250);
})();
